import { useMemo, useState } from "react";
import { Loader2, Pencil, Plus, Search, Ticket, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/layout/PageHeader";
import { CouponDialog } from "@/components/coupons/CouponDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Pagination } from "@/components/ui/pagination";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { isCouponUsable, useCoupons, useDeleteCoupon } from "@/hooks/use-coupons";
import { useSellers } from "@/hooks/use-sellers";
import { usePagination } from "@/hooks/use-pagination";
import { describeCallableError } from "@/lib/callables";
import { formatCurrency, formatDate, formatNumber, matches, toDate } from "@/lib/format";
import type { Coupon } from "@/types";

type Filter = "todos" | "ativos" | "expirados" | "carrinho" | "plataforma";

function describeDiscount(coupon: Coupon): string {
  if (coupon.discountType === "free_shipping") {
    if (coupon.freeShippingScope !== "products") return "Frete grátis";
    const count = coupon.freeShippingProductIds?.length ?? 0;
    return `Frete grátis em ${formatNumber(count)} ${count === 1 ? "produto" : "produtos"}`;
  }
  if (coupon.discountType === "percentage") return `${coupon.discount ?? 0}%`;
  return formatCurrency(coupon.discount);
}

export default function Coupons() {
  const coupons = useCoupons();
  const sellers = useSellers();
  const remove = useDeleteCoupon();

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("todos");
  const [editing, setEditing] = useState<Coupon | undefined>();
  const [creating, setCreating] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const sellerName = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of sellers.data ?? []) map.set(s.id, s.name ?? s.id);
    return map;
  }, [sellers.data]);

  // O prazo é conferido contra o momento em que a lista chegou: o render
  // precisa ser puro, e a lista é relida ao voltar para a aba.
  const loadedAt = coupons.dataUpdatedAt;

  const rows = useMemo(() => {
    return (coupons.data ?? []).filter((coupon) => {
      if (search && !matches(coupon.code, search) && !matches(coupon.description, search)) {
        return false;
      }
      const expires = toDate(coupon.expirationDate);
      switch (filter) {
        case "ativos":
          return isCouponUsable(coupon, loadedAt);
        case "expirados":
          return Boolean(expires && expires.getTime() < loadedAt);
        case "carrinho":
          return coupon.isVisibleAtCheckout === true;
        case "plataforma":
          return coupon.sponsoredBy === "platform";
        default:
          return true;
      }
    });
  }, [coupons.data, search, filter, loadedAt]);

  const pagination = usePagination(rows, `${search}|${filter}`);
  const removingTarget = rows.find((c) => c.id === removingId);

  async function confirmRemove() {
    if (!removingId) return;
    try {
      await remove.mutateAsync(removingId);
      toast.success("Cupom removido.");
      setRemovingId(null);
    } catch (error) {
      toast.error(describeCallableError(error));
    }
  }

  return (
    <>
      <PageHeader
        title="Cupons"
        description="Descontos da plataforma e dos estabelecimentos."
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus /> Novo cupom
          </Button>
        }
      />

      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por código ou descrição"
            className="pl-9"
            aria-label="Buscar cupom"
          />
        </div>
        <Select value={filter} onValueChange={(v) => setFilter(v as Filter)}>
          <SelectTrigger className="w-full sm:w-48" aria-label="Filtrar cupons">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="ativos">Válidos agora</SelectItem>
            <SelectItem value="expirados">Expirados</SelectItem>
            <SelectItem value="carrinho">Visíveis no carrinho</SelectItem>
            <SelectItem value="plataforma">Promoções do Zé Padoca</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0 sm:p-0">
          {coupons.isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={Ticket}
              title={search || filter !== "todos" ? "Nenhum cupom encontrado" : "Nenhum cupom cadastrado"}
              description={
                search || filter !== "todos"
                  ? "Ajuste a busca ou o filtro para ver outros resultados."
                  : "Crie um cupom para o cliente resgatar pelo código ou encontrar no carrinho."
              }
              action={
                !search && filter === "todos" ? (
                  <Button onClick={() => setCreating(true)}>
                    <Plus /> Criar o primeiro
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <Table className="table-stacked">
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead className="hidden md:table-cell">Estabelecimento</TableHead>
                  <TableHead>Desconto</TableHead>
                  <TableHead className="hidden lg:table-cell">Usos</TableHead>
                  <TableHead className="hidden md:table-cell">Validade</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead className="w-32" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagination.pageItems.map((coupon) => {
                  const expires = toDate(coupon.expirationDate);
                  const expired = Boolean(expires && expires.getTime() < loadedAt);
                  return (
                    <TableRow key={coupon.id}>
                      <TableCell className="cell-title">
                        <span className="font-mono font-bold">{coupon.code}</span>
                        {coupon.description ? (
                          <p className="truncate text-caption text-muted-foreground">
                            {coupon.description}
                          </p>
                        ) : null}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {coupon.fromSellers?.length
                          ? sellerName.get(coupon.fromSellers[0]) ?? coupon.establishment ?? "-"
                          : coupon.establishment || "Todos"}
                      </TableCell>
                      <TableCell className="font-semibold tabular-nums">
                        {describeDiscount(coupon)}
                        {coupon.minValue ? (
                          <span className="block text-caption font-normal text-muted-foreground">
                            mín. {formatCurrency(coupon.minValue)}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell tabular-nums">
                        {formatNumber(coupon.usageCount ?? 0)}
                        {coupon.usageLimit ? ` de ${formatNumber(coupon.usageLimit)}` : ""}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">{formatDate(coupon.expirationDate)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {expired ? (
                            <Badge variant="muted">Expirado</Badge>
                          ) : coupon.isActive ? (
                            <Badge variant="ok">Ativo</Badge>
                          ) : (
                            <Badge variant="muted">Inativo</Badge>
                          )}
                          {coupon.isVisibleAtCheckout ? (
                            <Badge variant="default">No carrinho</Badge>
                          ) : null}
                          {coupon.sponsoredBy === "platform" ? (
                            <Badge variant="outline">Zé Padoca paga</Badge>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="cell-actions">
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setEditing(coupon)}>
                            <Pencil /> Editar
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => setRemovingId(coupon.id)}
                            aria-label={`Remover ${coupon.code}`}
                          >
                            <Trash2 />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          <Pagination {...pagination.paginationProps} itemLabel="cupons" />
        </CardContent>
      </Card>

      {creating ? <CouponDialog open onOpenChange={() => setCreating(false)} /> : null}
      {editing ? (
        <CouponDialog coupon={editing} open onOpenChange={() => setEditing(undefined)} />
      ) : null}

      <AlertDialog open={Boolean(removingId)} onOpenChange={(open) => !open && setRemovingId(null)}>
        <AlertDialogContent>
          <AlertDialogTitle>Remover o cupom {removingTarget?.code}?</AlertDialogTitle>
          <AlertDialogDescription>
            Quem já resgatou o código perde o cupom. Se a intenção é apenas parar de oferecê-lo,
            prefira desativá-lo. Esta ação não tem desfazer.
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmRemove()} disabled={remove.isPending}>
              {remove.isPending ? <Loader2 className="animate-spin" /> : null}
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
